import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { SendHorizonal, Upload, Loader2 } from 'lucide-react';
import * as ww from '@wecom/jssdk';
import {
  formatSelectedData,
  generateExportContent,
  generateSendTitleBySelectedMetrics
} from '@/utils/dataFormatter';
import { type FormatConfig } from '@/utils/dataFormatter';
import { initWecom } from '@/utils/wecom';
import { WECOM_CORP_ID } from '@/config/env';
import { actionBarTabType } from '@/types';
import useCopilotValueStore from '@/stores/copilotValueStore';
import { confirm } from '@/components/confirm';
import useUserStore from '@/stores/userStore';
import dayjs from 'dayjs';
import { toast } from 'sonner';

const moudleNameMap = {
  aiAdvisorDiagnosis: ['overview', 'holdings', 'trading', 'recommendations'],
  // aiAdvisorCustomAssetAllocation: [],
  aiAdvisorRiskAssessment: ['marketInterpretation', 'productInterpretation', 'productComparison']
};

interface UniversalDataSenderProps {
  selectedMetrics: Record<string, boolean>;
  onSendComplete?: (success: boolean) => void;
  onExport?: () => void;
  disabled?: boolean;
  className?: string;
  tabType?: actionBarTabType;
  // 自定义格式化函数
  customFormatter?: (config: FormatConfig) => Promise<any[]>;
  // 按钮文本自定义
  sendText?: string;
  exportText?: string;
  // 是否显示发送按钮
  showSend?: boolean;
  // 是否显示导出按钮
  showExport?: boolean;
  fundData?: any;
  timeRange?: string;
}

const UniversalDataSender: React.FC<UniversalDataSenderProps> = ({
  selectedMetrics,
  onSendComplete,
  onExport,
  disabled = false,
  className = '',
  tabType = 'overview',
  customFormatter,
  sendText = '发送',
  exportText = '导出',
  showSend = true,
  showExport = true,
  fundData,
  timeRange
}) => {
  const [isSending, setIsSending] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const { userInfo } = useUserStore();

  const {
    copilotValue1,
    copilotValue2,
    copilotValue3,
    copilotValue4,
    copilotValue8,
    copilotValue9,
    copilotValue10,
    copilotValue11,
    copilotValue17
  } = useCopilotValueStore();
  const totalData = useMemo(() => {
    const mergedData = {
      ...copilotValue1,
      ...copilotValue2,
      ...copilotValue3,
      ...copilotValue4,
      ...copilotValue8,
      ...copilotValue9,
      ...copilotValue10,
      ...copilotValue11,
      ...copilotValue17
    };

    // 投顾洞察-产品推荐agent请求
    // if (tabType === 'productRecommend') {
    //   Object.assign(mergedData, copilotValue8);
    // }

    // 投顾洞察-产品对比agent请求
    if (tabType === 'productComparison') {
      Object.assign(mergedData, copilotValue9);
    }

    // 投顾洞察-产品解读agent请求
    if (tabType === 'productInterpretation') {
      Object.assign(mergedData, copilotValue10);
    }

    // 投顾洞察-市场解读agent请求
    if (tabType === 'marketInterpretation') {
      Object.assign(mergedData, copilotValue11);
    }
    return mergedData;
  }, [
    copilotValue1,
    copilotValue2,
    copilotValue3,
    copilotValue4,
    copilotValue8,
    copilotValue9,
    copilotValue10,
    copilotValue11,
    copilotValue17
  ]);

  const initWecomFn = async () => {
    await initWecom({
      corpId: WECOM_CORP_ID
    });
  };

  useEffect(() => {
    initWecomFn();
  }, []);

  // 发送数据
  const handleSend = useCallback(async () => {
    if (isSending) return;

    const sendDescription = generateSendTitleBySelectedMetrics(selectedMetrics);
    const ok = await confirm({
      title: '发送',
      description: `是否发送"${sendDescription}"卡片给客户？`
    });
    if (!ok) return;
    try {
      setIsSending(true);
      // 格式化数据
      const config: FormatConfig = {
        selectedMetrics,
        totalData,
        tabType,
        fundData,
        timeRange
      };

      const aiAdvisorRiskAssessmentArr = [
        'productRecommend',
        ...moudleNameMap.aiAdvisorRiskAssessment
      ];
      if (aiAdvisorRiskAssessmentArr.includes(tabType)) {
        config.totalData = {
          ...copilotValue8,
          ...config.totalData
        };
      }

      let formattedItems;
      if (customFormatter) {
        formattedItems = await customFormatter(config);
      } else {
        formattedItems = await formatSelectedData(config);
      }

      console.log('Formatted items:', formattedItems);

      // 分离文本和图片内容
      // const textItems = formattedItems.filter(item => item.type === 'text');
      // const imageItems = formattedItems.filter(item => item.type === 'image');

      // 先判断数组是否存在，避免报错
      const items = formattedItems || [];

      if (moudleNameMap.aiAdvisorDiagnosis.includes(tabType)) {
        await ww.sendChatMessage({
          msgtype: 'text',
          text: {
            content: `您好，这是根据您持仓情况做的分析，如果您有任何想法或疑问，都非常欢迎随时告诉我，我将在线为您解答。\n`
          }
        });
      } else if (moudleNameMap.aiAdvisorRiskAssessment.includes(tabType)) {
        await ww.sendChatMessage({
          msgtype: 'text',
          text: {
            content: `您好，这是根据您的需求做的分析，如果您有任何想法或疑问，都非常欢迎随时告诉我，我将在线为您解答。\n`
          }
        });
      }

      // 使用for...of循环替代forEach，更清晰地处理异步顺序
      for (const item of items) {
        try {
          if (item.type === 'text') {
            await ww.sendChatMessage({
              msgtype: 'text',
              text: {
                content: `【${item.title}】\n${item.content}\n`
              }
            });
          }

          if (item.type === 'image') {
            if (item.imageId) {
              // 先发送标题文本
              await ww.sendChatMessage({
                msgtype: 'text',
                text: {
                  content: `【${item.title}】\n`
                }
              });
              // 再发送图片，确保标题发送完成后才发图片
              await ww.sendChatMessage({
                msgtype: 'image',
                image: {
                  mediaid: item.imageId
                }
              });
              await ww.sendChatMessage({
                msgtype: 'text',
                text: {
                  content: `\n`
                }
              });
            } else {
              await ww.sendChatMessage({
                msgtype: 'text',
                text: {
                  content: `【${item.title}】\n 发送失败，缺少图片ID`
                }
              });
            }
          }

          // 每发送完一个消息，可以在这里添加延迟（如果需要）
          // await new Promise(resolve => setTimeout(resolve, 500));
        } catch (error) {
          console.error(`发送消息失败:`, error);
          // 根据需要决定是否继续发送下一个，还是终止
          // throw error; // 终止发送
        }
      }

      onSendComplete?.(true);
      console.log('Data sent successfully');
    } catch (error) {
      console.error('Failed to send data:', error);
      onSendComplete?.(false);
    } finally {
      setIsSending(false);
    }
  }, [selectedMetrics, totalData, isSending, onSendComplete, tabType, customFormatter]);

  // 导出数据
  const handleExport = useCallback(async () => {
    if (isExporting) return;

    const ok = await confirm({
      title: '导出',
      description: '是否导出完整报告？'
    });

    if (ok) {
      try {
        setIsExporting(true);
        // 持仓诊断导出
        if (['overview', 'holdings', 'trading', 'recommendations'].includes(tabType)) {
          const moduleName = {
            overview: '总览',
            holdings: '持仓分析',
            trading: '交易行为',
            recommendations: '优化推荐'
          };
          // 持仓深度诊断 导出持仓深度诊断报告
          const diagnosisPageEl = document.querySelector(`#${tabType}-tab-page`) as HTMLElement;
          if (diagnosisPageEl) {
            generateExportContent(
              diagnosisPageEl,
              `${userInfo.customer_name}_${
                moduleName[tabType as keyof typeof moduleName]
              }模块诊断分析_${dayjs().format('YYYY-MM-DD HH:mm:ss')}.jpg`,
              () => {
                onExport?.();
              }
            );
          } else {
            toast.error('目标元素不存在，导出失败，请稍后重试');
          }
        }
        // 投顾洞察导出
        if (
          ['productComparison', 'marketInterpretation', 'productInterpretation'].includes(tabType)
        ) {
          const moduleName = {
            productComparison: '产品对比',
            marketInterpretation: '市场解读',
            productInterpretation: '产品解读'
          };
          // 投顾洞察 导出投顾洞察报告
          const riskAssessmentPageEl = document.querySelector(
            `#${tabType}-tab-page`
          ) as HTMLElement;
          if (riskAssessmentPageEl) {
            generateExportContent(
              riskAssessmentPageEl,
              `${userInfo.customer_name}_${
                moduleName[tabType as keyof typeof moduleName]
              }模块投顾洞察_${dayjs().format('YYYY-MM-DD HH:mm:ss')}.jpg`,
              () => {
                onExport?.();
              }
            );
          } else {
            toast.error('目标元素不存在，导出失败，请稍后重试');
          }
        }
      } catch (error) {
        console.error('Failed to export data:', error);
      } finally {
        setIsExporting(false);
      }
    } else {
      console.log('用户取消了');
    }
  }, [selectedMetrics, totalData, isExporting, onExport, tabType, customFormatter, fundData]);

  // 检查是否有选中的内容
  const hasSelectedContent = Object.values(selectedMetrics).some(selected => selected);

  return (
    <div className={`flex gap-2 ${className}`}>
      {showSend && (
        <Button
          className="h-6 rounded bg-gradient-to-r from-[#5D2BC7] to-[#A644CA] px-2 py-1 text-sm text-white shadow-sm disabled:opacity-50"
          disabled={disabled || !hasSelectedContent || isSending}
          onClick={handleSend}
        >
          {isSending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <SendHorizonal className="h-4 w-4" />
          )}
          <span>{isSending ? '发送中...' : sendText}</span>
        </Button>
      )}

      {showExport && (
        <Button
          className="h-6 rounded border border-[#5959EB] px-2 py-1 text-sm text-[#5959EB] disabled:opacity-50"
          disabled={isExporting}
          variant="outline"
          onClick={handleExport}
        >
          {isExporting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Upload className="h-4 w-4" />
          )}
          <span>{isExporting ? '导出中...' : exportText}</span>
        </Button>
      )}
    </div>
  );
};

export default UniversalDataSender;
